import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import FileUpload from '../../../components/ui/FileUpload';

const BulkImportModal = ({ isOpen, onClose, onImport }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);

  const handleFileUpload = (file) => {
    setCsvFile(file);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        
        const nameIndex = headers?.indexOf('name');
        const hexIndex = headers?.indexOf('hex') !== -1 ? headers?.indexOf('hex') : headers?.indexOf('hexcode');
        const groupIndex = headers?.indexOf('group');

        if (nameIndex === -1 || hexIndex === -1) {
          setErrors(['CSV must contain "name" and "hex" columns']);
          return;
        }

        const parsedData = [];
        const parseErrors = [];

        for (let i = 1; i < Math.min(lines?.length, 11); i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const name = values?.[nameIndex];
          let hex = values?.[hexIndex];
          const group = groupIndex !== -1 ? values?.[groupIndex] : '';

          if (!hex?.startsWith('#')) {
            hex = '#' + hex;
          }

          const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
          if (!hexRegex?.test(hex)) {
            parseErrors?.push(`Row ${i + 1}: Invalid hex code "${hex}"`);
            continue;
          }

          parsedData?.push({
            name: name || `Color ${i}`,
            hexCode: hex,
            group: group || ''
          });
        }

        setPreviewData(parsedData);
        setErrors(parseErrors);
      } catch (error) {
        setErrors(['Failed to parse CSV file. Please check the format.']);
      }
    };

    reader?.readAsText(file);
  };

  const handleImport = () => {
    if (previewData?.length === 0) {
      setErrors(['No valid data to import']);
      return;
    }

    const colorsToImport = previewData?.map(color => ({
      ...color,
      id: Date.now() + Math.random(),
      usageCount: 0,
      createdAt: new Date()?.toISOString(),
      swatchImage: null
    }));

    onImport(colorsToImport);
    handleClose();
  };

  const handleClose = () => {
    setCsvFile(null);
    setPreviewData([]);
    setErrors([]);
    onClose();
  };

  const downloadTemplate = () => {
    const template = 'name,hex,group\nOcean Blue,#0EA5E9,base-colors\nSunset Orange,#F97316,popular\nForest Green,#10B981,seasonal';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'color-import-template.csv';
    a?.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-3xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">
            Bulk Import Colors
          </h2>
          <button
            onClick={handleClose}
            className="text-text-secondary hover:text-text-primary transition-colors duration-150"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-muted border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-text-primary mb-2">
                  CSV Format Requirements
                </h3>
                <ul className="text-sm text-text-secondary space-y-1">
                  <li>• Required columns: name, hex (or hexcode)</li>
                  <li>• Optional column: group</li>
                  <li>• Hex codes can include or omit the # symbol</li>
                  <li>• Maximum 100 colors per import</li>
                </ul>
                <Button
                  variant="link"
                  size="sm"
                  onClick={downloadTemplate}
                  iconName="Download"
                  iconPosition="left"
                  iconSize={14}
                  className="mt-2 p-0"
                >
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          <FileUpload
            label="Upload CSV File"
            accept=".csv,text/csv"
            onUpload={handleFileUpload}
            description=""
            error=""
          />

          {errors?.length > 0 && (
            <div className="bg-error/10 border border-error rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-error mb-2">
                    Import Errors
                  </h3>
                  <ul className="text-sm text-error space-y-1">
                    {errors?.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {previewData?.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-text-primary">
                Preview ({previewData?.length} colors)
              </h3>
              <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-secondary">
                        Color
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-secondary">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-secondary">
                        Hex Code
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-text-secondary">
                        Group
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData?.map((color, index) => (
                      <tr key={index} className="border-b border-border last:border-0">
                        <td className="px-4 py-2">
                          <div
                            className="w-8 h-8 rounded border border-border"
                            style={{ backgroundColor: color?.hexCode }}
                          />
                        </td>
                        <td className="px-4 py-2 text-sm text-text-primary">
                          {color?.name}
                        </td>
                        <td className="px-4 py-2">
                          <code className="text-sm text-text-secondary font-mono">
                            {color?.hexCode}
                          </code>
                        </td>
                        <td className="px-4 py-2 text-sm text-text-secondary">
                          {color?.group || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleImport}
              disabled={previewData?.length === 0}
              iconName="Upload"
              iconPosition="left"
              iconSize={16}
            >
              Import {previewData?.length} Colors
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;